/* LocaleContext.jsx
   ----------------------------------------------------------------------
   Reads the language from the URL and hands it to the whole tree.

   In a component:
     const { t, lp, locale, isRtl } = useLocale();
     <h1>{t({ en: "Trips", ar: "الرحلات", ru: "Поездки" })}</h1>
     <a href={lp("/#contact")}>…</a>

   For a whole data record (src/data/*.js):
     const trip = useL(DUBAI_WINTER_CAMP);   // every triple resolved
     <h3>{trip.name}</h3>

   Pass the RAW imported record (not the resolved one) to anything that
   writes to Firestore or Stripe; those payloads must stay English.
   See resolveEn in locale.js. */

import React, { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_LOCALE,
  LOCALE_META,
  localeFromPath,
  localizePath,
  resolve,
} from "./locale";

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const { pathname } = useLocation();
  const locale = localeFromPath(pathname);
  const meta = LOCALE_META[locale] || LOCALE_META[DEFAULT_LOCALE];

  /* <html lang> and <html dir> drive the per-language font stacks and
     the whole right-to-left mirroring in index.css. SEO.jsx sets the
     same pair through Helmet for crawlers; this keeps them correct on
     routes that render before Helmet commits, and on the 404 page. */
  useEffect(() => {
    const html = document.documentElement;
    html.lang = meta.htmlLang;
    html.dir = meta.dir;
  }, [meta.htmlLang, meta.dir]);

  const value = useMemo(() => ({
    locale,
    meta,
    dir: meta.dir,
    isRtl: meta.dir === "rtl",
    /* copy triple → string */
    t: (copy) => resolve(copy, locale),
    /* in-site path → language-prefixed path */
    lp: (path) => localizePath(path, locale),
  }), [locale, meta]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const ctx = useContext(LocaleContext);
  if (!ctx) {
    /* Safety net: a component rendered outside the provider still gets
       working English rather than a crash. */
    return {
      locale: DEFAULT_LOCALE,
      meta: LOCALE_META[DEFAULT_LOCALE],
      dir: "ltr",
      isRtl: false,
      t: (copy) => resolve(copy, DEFAULT_LOCALE),
      lp: (path) => localizePath(path, DEFAULT_LOCALE),
    };
  }
  return ctx;
}

/* Resolve a data record (or array) for the current language. */
export function useL(data) {
  const { locale } = useLocale();
  return useMemo(() => resolve(data, locale), [data, locale]);
}
